import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, Link, Typography } from '@mui/material';

import AppleDark from '~/assets/icons/svgs/apple-logo.svg?react';
import GooglePlay from '~/assets/icons/svgs/google-play-logo.svg?react';
import { Text } from '~/components/ui/text';
import { getAppsReducer } from '~/store/reducers/previewScreens/apps/selectors';
import { COLORS } from '~/styles/colors';
import { mainPreviewContainerStyles } from '~/styles/common';

import { RenderSocialButtons } from './components/RenderSocialButtons';

type AppsPreviewScreenProps = {
  isMyMicrosite?: boolean;
};
export const AppsPreviewScreen = ({ isMyMicrosite }: AppsPreviewScreenProps) => {
  const appsState = useSelector(getAppsReducer);

  const { t } = useTranslation();

  const {
    logo,
    micrositeStyles,
    bacgkroundColor,
    fonts: { title, text },
    platformLinks: { google, apple },
    appInformation: { appName, developerCompany, description, website },
  } = appsState;

  const showMainBlock = description || logo?.path || apple || google || appName;

  return (
    <Box
      sx={{
        ...mainPreviewContainerStyles,
        alignItems: 'normal',
        background: bacgkroundColor[0],
        ...micrositeStyles?.container,
        margin: isMyMicrosite ? 0 : '12px',
        borderRadius: isMyMicrosite ? 0 : '36px',
        height: isMyMicrosite ? 'auto' : 'calc(100% - 30px)',
        minHeight: isMyMicrosite ? '100dvh' : undefined,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', mt: 4, textAlign: 'center', minWidth: '100%' }}>
        <Text
          sx={{ fontSize: 10, mb: 3, fontFamily: text, pl: 0.5, pr: 0.5, wordBreak: 'break-word', color: COLORS.white }}
        >
          {developerCompany}
        </Text>
        {!!showMainBlock && (
          <Box
            sx={{
              backgroundColor: COLORS.white,
              borderRadius: '13px',
              color: COLORS.black,
              fontSize: 10,
              padding: '8px 10px',
              margin: '16px 10px',
              mt: logo?.path ? 6 : 1,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
              {!!logo?.path && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: COLORS.white,
                    position: 'absolute',
                    top: '-50px',
                    filter: `drop-shadow(0px 0px 5px ${COLORS.blackShadow});`,
                    borderRadius: '6px',
                    width: '75px',
                    height: '75px',
                  }}
                >
                  <img
                    src={logo.path}
                    alt="logo"
                    style={{ borderRadius: '3px', width: '69px', height: '69px', maxWidth: '100%' }}
                  />
                </Box>
              )}
            </Box>
            <Box
              sx={{
                mt: logo?.path ? 7 : 0,
                fontWeight: 'bold',
                fontSize: 16,
                mb: 2,
                fontFamily: title,
                wordBreak: 'break-word',
              }}
            >
              {appName}
            </Box>
            <Typography
              sx={{
                fontSize: 10,
                padding: 1,
                fontFamily: text,
                mb: 1,
                wordBreak: 'break-word',
              }}
            >
              {description}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {!!google && (
                <RenderSocialButtons
                  text={t('socialButtons:googlePlayText')}
                  title={t('socialButtons:googlePlayTitle')}
                  Icon={GooglePlay}
                  href={google}
                />
              )}
              {!!apple && (
                <RenderSocialButtons
                  text={t('socialButtons:appleText')}
                  title={t('socialButtons:appleTitle')}
                  Icon={AppleDark}
                  href={apple}
                />
              )}
            </Box>
          </Box>
        )}
        <Link
          href={website}
          sx={{
            fontSize: 11,
            mt: 3,
            paddingBottom: 2,
            pl: 0.5,
            pr: 0.5,
            color: COLORS.white,
            textDecoration: 'none',
            fontFamily: text,
          }}
        >
          {website}
        </Link>
      </Box>
    </Box>
  );
};
